'use client';
// src/pages/Unauthorized.jsx
// Generic "you can't be here" page. Reached via VerifiedCoachRoute when a
// non-coach role lands on a coach-gated path that ISN'T behind the
// RequireCoachRole guard (which silently redirects parents to /parent/teams
// instead of showing this page). Today the main caller is /notifications.

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ShieldExclamationIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import useLogout from '@/hooks/useLogout';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function Unauthorized() {
  const { userProfile, isAuthenticated } = useAuth();
  const logout = useLogout();

  // /dashboard is now the universal team picker for every user —
  // we no longer have separate parent/coach landing pages. Copy
  // still adapts based on signup-role hint so parents get the
  // friendlier "your account is set up as a parent" wording.
  const isParent = String(userProfile?.role || '').toLowerCase() === 'parent';
  const homePath = '/dashboard';
  const homeLabel = isParent ? 'Go to my teams' : 'Return home';

  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {/* Icon chip */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
          <ShieldExclamationIcon className="h-8 w-8 text-[#5a8a1f]" />
        </div>

        <p
          className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          Unauthorized
        </p>
        <h1
          className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14] mb-3"
          style={{ fontFamily: HEAD_FONT }}
        >
          You don't have access to this page
        </h1>

        <p className="text-sm text-zinc-500 leading-relaxed mb-8">
          {isParent
            ? "This area is for coaches and team admins. Your account is set up as a parent — head back to your teams to see your child's schedule and messages."
            : 'Your account doesn\'t have permission to view this page. If you think this is wrong, ask the team owner or club admin to grant you access.'}
        </p>

        {/* Primary CTA */}
        <Link
          to={homePath}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase tracking-wide shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {homeLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>

        {/* Secondary — sign out, in case the user landed here on the wrong account */}
        {isAuthenticated && (
          <button
            type="button"
            onClick={() => logout()}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white text-zinc-600 hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 hover:text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
            Sign out and switch accounts
          </button>
        )}

        {/* Footer help link */}
        <p className="mt-8 text-xs text-zinc-400">
          Need help?{' '}
          <a
            href="mailto:hello@getscrimly.com?subject=Access%20issue"
            className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] underline"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
